import { useState } from 'react'
import { useOutletContext, useParams } from 'react-router-dom'

import { Box, Loading, NotFound } from '@factorialco/deprecated-design-system'
import { SectionHeader } from '@factorialco/f0-react/dist/experimental'
import { Pencil } from '@factorialco/f0-react/icons/app'
import { ProjectManagementProjectDetailsPagePeoplePageDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import ProjectWorkersPanel from 'modules/project_management/components/ProjectWorkersPanel'
import ProjectPeopleDataCollection from 'modules/project_management/components/tables/ProjectPeopleDataCollection'
import { ProjectWorkersPanelData } from 'modules/project_management/components/tables/ProjectPeopleDataCollection/types'

import RatesSettingsSidepanel from './RatesSettingsSidepanel'

const ProjectEmployeesPage = () => {
  const { id } = useParams()
  const projectId = Number(id)

  const [showProjectWorkersPanel, setShowProjectWorkersPanel] = useState<boolean>(false)
  const [projectWorkersPanelData, setProjectWorkersPanelData] = useState<
    ProjectWorkersPanelData | undefined
  >(undefined)
  const [showSidePanel, setShowSidePanel] = useState(false)

  const {
    employee,
    company: { id: companyId },
  } = useCurrentSession()

  const { data, loading } = useQuery(ProjectManagementProjectDetailsPagePeoplePageDocument, {
    variables: { companyId, projectId },
  })

  const isTotalProfitabilityEnabled = !!data?.features.featuresConnection.nodes.find(
    (feature) => feature.name === 'dev_projects_total_profitability'
  )?.enabled

  const { hasCost, hasExpenses, hasFlexibleTracking } = useOutletContext<{
    hasBudgetFeature: boolean
    hasCost: boolean
    hasExpenses: boolean
    hasFlexibleTracking: boolean
  }>()

  useEffect(() => {
    track(trackingEvents.projectManagement.projects.goToProjectDetailsEmployees)
  }, [])

  if (loading) {
    return <Loading />
  }

  const project = data?.projectManagement.project

  if (!project) {
    return (
      <NotFound
        title={i18n.t('errors.page_not_found.title')}
        description={i18n.t('errors.page_not_found.description')}
      />
    )
  }

  const projectCurrency = project.legalEntity.currency
  const isIndividualRateType = project.billableRateType === 'individual'

  const canManageProjects = data?.projectManagement._policies.projectManage.can ?? false
  const canAssignEmployees = project?._policies?.assignUnassignProjectWorkers.can ?? false
  const canSeeLaborCosts = project?._policies?.seeLaborCost.can ?? false
  const isOwner = project?._policies?.assignOwner.can ?? false

  const hasTimeTrackingSection =
    data?.attendance._policies.sidebarSeeAttendanceCompanyTopLevelItem.can ?? false
  const hasFixedCostPlanning = isTotalProfitabilityEnabled
    ? project?.budgetStrategy?.budgetType === 'project_fixed_cost'
    : !!project?.fixedCostPlanning?.id

  const canExportData =
    canManageProjects &&
    !!employee &&
    (hasTimeTrackingSection || hasExpenses || hasFlexibleTracking)

  const canSeeSpendings = isOwner && hasExpenses && hasCost
  const canManageRates = data?.projectManagement._policies.rateManage.can ?? false
  const canSeeRates = canManageRates && !hasFixedCostPlanning
  const canAssignSingleEmployee = canAssignEmployees && project.employeesAssignment !== 'company'

  const canManageProject = project?._policies?.assignEditor.can ?? false

  const openProjectWorkersPanel = (values: ProjectWorkersPanelData) => {
    setShowProjectWorkersPanel(true)
    setProjectWorkersPanelData({
      ...values,
    })

    track(trackingEvents.projectManagement.projectWorkers.openProjectWorkersPanel, {
      is_owner: isOwner,
      can_see_costs: canSeeLaborCosts,
    })
  }

  return (
    <>
      {canSeeRates && (
        <Box paddingBottom="s24">
          <SectionHeader
            title={i18n.t('project_management.people_and_rates.header.title')}
            description={i18n.t('project_management.people_and_rates.header.description')}
            link={{
              href: `https://help.factorialhr.com/${i18n.t(
                'project_management.projects.lower_case_title'
              )}`,
              label: i18n.t('project_management.people_and_rates.header.secondary_button'),
            }}
            action={
              canManageRates
                ? {
                    icon: Pencil,
                    label: i18n.t('project_management.people_and_rates.header.settings_button'),
                    onClick: () => {
                      track(trackingEvents.projectManagement.jobsAndRates.clickOnRatesSettings)
                      setShowSidePanel(true)
                    },
                  }
                : undefined
            }
          />
        </Box>
      )}
      <ProjectPeopleDataCollection
        projectCurrency={projectCurrency}
        canAssignEmployees={canAssignEmployees}
        canAssignSingleEmployee={canAssignSingleEmployee}
        canExportData={canExportData}
        canManageProject={canManageProject}
        canSeeLaborCosts={canSeeLaborCosts}
        canSeeRates={canSeeRates}
        canSeeSpendings={canSeeSpendings}
        onClick={openProjectWorkersPanel}
        isTotalProfitabilityEnabled={isTotalProfitabilityEnabled}
      />
      {projectWorkersPanelData && (
        <ProjectWorkersPanel
          data={projectWorkersPanelData}
          isIndividualRateType={isIndividualRateType}
          onClose={() => {
            setShowProjectWorkersPanel(false)
          }}
          showProjectWorkersPanel={showProjectWorkersPanel}
        />
      )}
      <RatesSettingsSidepanel
        isOpen={showSidePanel}
        onClose={() => setShowSidePanel(false)}
        projectId={project.id}
      />
    </>
  )
}

export default ProjectEmployeesPage
